import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    ArrowLeft,
    Upload,
    FolderOpen,
    Plug,
    Check,
    CheckCircle2,
    FileSpreadsheet,
    FileText,
    FileImage,
    File as FileIcon,
    FilePlus,
    X,
    RefreshCw,
    Library,
    Loader2,
    AlertCircle,
    AlertTriangle,
    Plus
} from 'lucide-react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';

const DataSourceConfiguration = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { controlSpec, fileId, file, controlType, draftControlId, skipToConfirmation, savedControlData: preloadedControlData } = location.state || {};

    const [dataSourceType, setDataSourceType] = useState('');
    const [uploadedDataFiles, setUploadedDataFiles] = useState([]);
    const [syncDirectory, setSyncDirectory] = useState('');
    const [continuousSync, setContinuousSync] = useState(false);
    const [selectedIntegrations, setSelectedIntegrations] = useState([]);
    const [loadingTestFiles, setLoadingTestFiles] = useState(false);
    const [modal, setModal] = useState({ open: false, type: 'error', message: '' });
    const [showConfirmation, setShowConfirmation] = useState(!!skipToConfirmation);
    const [savedControlData, setSavedControlData] = useState(preloadedControlData || null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Check if all files are fully uploaded
    const allFilesUploaded = uploadedDataFiles.length === 0 ||
        uploadedDataFiles.every(f => f.status === 'completed');

    // Check if any files are currently uploading
    const hasFilesUploading = uploadedDataFiles.some(f => f.status === 'uploading');

    // Test files available in public folder
    const testFiles = [
        { name: 'Asana IronClad Review - 09_30_2024.xlsx', path: '/test-files/Asana IronClad Review - 09_30_2024.xlsx', type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        { name: 'Asana IronClad Report Review - Asana.pdf', path: '/test-files/Asana IronClad Report Review - Asana.pdf', type: 'application/pdf' },
        { name: 'Asana (FA-01.12) Review for Material Contracts greater $250K.pdf', path: '/test-files/Asana (FA-01.12) Review for Material Contracts greater $250K.pdf', type: 'application/pdf' }
    ];

    const loadTestFiles = async () => {
        // Prevent loading if already loading
        if (loadingTestFiles) {
            return;
        }

        setLoadingTestFiles(true);
        try {
            const newFiles = [];
            for (const testFile of testFiles) {
                const response = await fetch(testFile.path);
                if (!response.ok) {
                    console.error(`Failed to fetch ${testFile.name}`);
                    continue;
                }
                const blob = await response.blob();

                // Create a proper File object with the correct name
                const fileObject = new File([blob], testFile.name, { type: testFile.type });

                const uploadedFile = {
                    id: `test-file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    name: testFile.name,
                    size: `${(blob.size / 1024 / 1024).toFixed(1)} MB`,
                    status: 'completed',
                    progress: 100,
                    fileObject: fileObject
                };

                newFiles.push(uploadedFile);
            }

            // Add all files at once, filtering out any that already exist by name
            setUploadedDataFiles(prev => {
                const existingNames = new Set(prev.map(f => f.name));
                const uniqueNewFiles = newFiles.filter(f => !existingNames.has(f.name));
                return [...prev, ...uniqueNewFiles];
            });
        } catch (error) {
            console.error('Error loading test files:', error);
            setModal({ open: true, type: 'error', message: 'Failed to load test files: ' + error.message });
        } finally {
            setLoadingTestFiles(false);
        }
    };

    const systemIntegrations = [
        { id: 'erp-sap', name: 'SAP ERP', type: 'ERP', connected: true, color: 'blue' },
        { id: 'crm-salesforce', name: 'Salesforce CRM', type: 'CRM', connected: false, color: 'green' },
        { id: 'hris-workday', name: 'Workday HRIS', type: 'HRIS', connected: true, color: 'orange' },
        { id: 'fin-netsuite', name: 'NetSuite Financial', type: 'Financial', connected: false, color: 'purple' },
        { id: 'audit-board', name: 'AuditBoard', type: 'Audit Platform', connected: false, color: 'red' },
    ];

    const handleDataFileUpload = (files) => {
        Array.from(files).forEach((file, index) => {
            const uploadedFile = {
                id: `data-file-${Date.now()}-${index}`,
                name: file.name,
                size: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
                status: 'uploading',
                progress: 0,
                fileObject: file // Store actual file for upload
            };

            setUploadedDataFiles(prev => [...prev, uploadedFile]);

            // Set dataSourceType to 'upload' when files are added
            setDataSourceType('upload');

            // Simulate upload progress
            const progressInterval = setInterval(() => {
                setUploadedDataFiles(prev => prev.map(f => {
                    if (f.id === uploadedFile.id) {
                        const newProgress = Math.min(f.progress + Math.random() * 30, 100);
                        const newStatus = newProgress === 100 ? 'completed' : 'uploading';
                        return { ...f, progress: newProgress, status: newStatus };
                    }
                    return f;
                }));
            }, 500);

            // Complete upload after 2 seconds
            setTimeout(() => {
                clearInterval(progressInterval);
                setUploadedDataFiles(prev => prev.map(f =>
                    f.id === uploadedFile.id ? { ...f, status: 'completed', progress: 100 } : f
                ));
            }, 2000);
        });
    };

    const removeDataFile = (fileId) => {
        setUploadedDataFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const toggleIntegration = (integrationId) => {
        setSelectedIntegrations(prev =>
            prev.includes(integrationId)
                ? prev.filter(id => id !== integrationId)
                : [...prev, integrationId]
        );
    };

    const handleBack = () => {
        navigate('/sox-controls/create-wizard/test-steps', {
            state: { controlSpec, fileId, file, draftControlId }
        });
    };

    const handleComplete = async () => {
        // Prevent double submission
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        try {
            // Prepare data source config based on type
            let dataSourceConfig = {};
            if (dataSourceType === 'upload') {
                // Don't include files here - they will be added by the upload endpoint
                // to prevent duplication (files were being saved twice: once here, once in upload)
                dataSourceConfig = {
                    files: []
                };
            } else if (dataSourceType === 'directory') {
                dataSourceConfig = {
                    path: syncDirectory,
                    continuousSync: continuousSync
                };
            } else if (!isManualControl) {
                dataSourceConfig = {
                    integrations: selectedIntegrations
                };
            }

            // Prepare control data
            const controlData = {
                control_id: controlSpec?.control_id || 'UNKNOWN',
                title: controlSpec?.control_title || 'Untitled Control',
                objective: controlSpec?.control_objective || '',
                control_wording_text: controlSpec?.control_wording_text || '',
                control_type: controlType || 'manual',
                frequency: controlSpec?.frequency || '',
                test_steps: controlSpec?.test_steps || [],
                data_source_type: dataSourceType || (isManualControl ? '' : 'integration'),
                data_source_config: dataSourceConfig,
                control_upload_id: fileId ? parseInt(fileId, 10) : null,
                // Include new mandatory fields
                sox_process: controlSpec?.sox_process || '',
                control_performer: controlSpec?.control_performer || '',
                significance: controlSpec?.significance || ''
            };

            // Get user hash from localStorage
            const userHash = localStorage.getItem('userHash');

            // 1. Save Control Metadata
            let response;
            if (draftControlId) {
                // Activating a draft — use PUT to update existing control
                controlData.status = 'active';
                response = await axiosInstance.put(`/api/sox-controls/${draftControlId}`, controlData, {
                    headers: {
                        'Authorization': userHash || ''
                    }
                });
                console.log('Draft control activated:', response.data);
            } else {
                // New control — use POST
                response = await axiosInstance.post('/api/sox-controls', controlData, {
                    headers: {
                        'Authorization': userHash || ''
                    }
                });
                console.log('Control saved successfully:', response.data);
            }
            const newControlId = draftControlId || response.data.id;

            // 2. Upload Files (if applicable)
            if (dataSourceType === 'upload' && uploadedDataFiles.length > 0) {
                const formData = new FormData();
                let hasFiles = false;

                uploadedDataFiles.forEach(file => {
                    if (file.fileObject) {
                        formData.append('files', file.fileObject);
                        hasFiles = true;
                    }
                });

                if (hasFiles) {
                    try {
                        await axiosInstance.post(`/api/sox-controls/${newControlId}/data-files`, formData, {
                            headers: {
                                'Authorization': userHash || '',
                                'Content-Type': 'multipart/form-data'
                            }
                        });
                        console.log('Files uploaded successfully');
                    } catch (uploadErr) {
                        console.error('Failed to upload data files:', uploadErr);
                        // We don't throw here to ensure we still navigate, but maybe show a warning
                        setModal({ open: true, type: 'warning', message: 'Control saved, but some files failed to upload.' });
                        setIsSubmitting(false);
                        return; // Don't navigate yet, let user see the warning
                    }
                }
            }

            // Show confirmation step instead of navigating
            setSavedControlData({
                title: controlData.title,
                control_id: controlData.control_id,
                control_type: controlData.control_type,
                control_wording: controlData.control_wording_text,
                objective: controlData.objective,
                test_steps: controlData.test_steps,
                filesCount: 0,
                integrationsCount: 0,
                // Include new mandatory fields for display
                sox_process: controlData.sox_process,
                control_performer: controlData.control_performer,
                significance: controlData.significance
            });
            setShowConfirmation(true);
        } catch (error) {
            console.error('Error saving control:', error);
            setModal({ open: true, type: 'error', message: resolve403ErrorMessage(error, 'Failed to save control. Please try again.') });
            setIsSubmitting(false);
        }
    };

    const resetWizard = () => {
        navigate('/sox-controls/create-wizard');
    };

    const getFileIcon = (filename) => {
        const ext = filename.split('.').pop()?.toLowerCase();
        if (['xlsx', 'xls', 'csv'].includes(ext)) return { Icon: FileSpreadsheet, color: 'text-green-600' };
        if (['pdf'].includes(ext)) return { Icon: FileText, color: 'text-red-600' };
        if (['doc', 'docx'].includes(ext)) return { Icon: FileText, color: 'text-blue-600' };
        if (['png', 'jpg', 'jpeg'].includes(ext)) return { Icon: FileImage, color: 'text-purple-600' };
        return { Icon: FileIcon, color: 'text-gray-600' };
    };

    const isManualControl = !controlType || controlType === 'manual';

    // Confirmation Step UI
    if (showConfirmation && savedControlData) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                {/* Top Bar */}
                <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between sticky top-0 z-10">
                    <div className="flex items-center gap-4">
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">Control Creation Wizard</h1>
                            <p className="text-sm text-gray-500">Control created successfully</p>
                        </div>
                    </div>
                </div>

                {/* Confirmation Content */}
                <div className="flex-1 max-w-4xl mx-auto w-full px-8 py-12">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-12 text-center">
                            {/* Success Icon */}
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle2 className="w-10 h-10 text-green-600" />
                            </div>

                            <h2 className="text-3xl font-semibold mb-4 text-gray-900">Control Created Successfully!</h2>
                            <p className="text-gray-500 mb-8 max-w-lg mx-auto">
                                Your control has been created and configured. You can now view it in the controls list or create another control.
                            </p>

                            {/* Control Summary Card */}
                            <div className="bg-white border border-gray-200 rounded-xl max-w-2xl mx-auto mb-8 text-left">
                                <div className="px-6 py-4 border-b border-gray-100">
                                    <h3 className="text-lg font-semibold text-gray-900">Control Summary</h3>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">Control Title</p>
                                            <p className="font-medium text-gray-900">{savedControlData.title}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">Control Type</p>
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                                                {savedControlData.control_type}
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Control ID</p>
                                        <p className="text-sm text-gray-900">{savedControlData.control_id}</p>
                                    </div>
                                    {savedControlData.control_wording && (
                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">Control Wording</p>
                                            <p className="text-sm text-gray-900">{savedControlData.control_wording}</p>
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Control Objective</p>
                                        <p className="text-sm text-gray-900">{savedControlData.objective || 'Not specified'}</p>
                                    </div>
                                    {/* New Mandatory Fields */}
                                    <div className="grid grid-cols-3 gap-4 border-t border-gray-100 pt-4">
                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">SOX Process</p>
                                            <p className="text-sm text-gray-900">{savedControlData.sox_process || 'Not specified'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">Control Performer</p>
                                            <p className="text-sm text-gray-900">{savedControlData.control_performer || 'Not specified'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">Significance</p>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                                                savedControlData.significance === 'key' ? 'bg-red-100 text-red-800' : 
                                                savedControlData.significance === 'non-key' ? 'bg-blue-100 text-blue-800' : 
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {savedControlData.significance || 'Not specified'}
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Test Steps</p>
                                        <p className="text-sm text-gray-900">
                                            {savedControlData.test_steps && savedControlData.test_steps.length > 0
                                                ? `${savedControlData.test_steps.length} test step(s) configured`
                                                : 'No test steps'}
                                        </p>
                                    </div>
                                    {savedControlData.control_type === 'manual' && savedControlData.filesCount > 0 && (
                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">Data Files</p>
                                            <p className="text-sm text-gray-900">{savedControlData.filesCount} file(s) uploaded</p>
                                        </div>
                                    )}
                                    {savedControlData.control_type !== 'manual' && savedControlData.integrationsCount > 0 && (
                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">Integrations</p>
                                            <p className="text-sm text-gray-900">{savedControlData.integrationsCount} integration(s) connected</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-center gap-4">
                                <button
                                    onClick={() => navigate('/sox-controls')}
                                    className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    View All Controls
                                </button>
                                <button
                                    onClick={resetWizard}
                                    className="px-6 py-2.5 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 transition-colors inline-flex items-center gap-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    Create Another Control
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Top Bar */}
            <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between sticky top-0 z-10">
                <div className="flex items-center gap-4">
                    <button
                        onClick={handleBack}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5 text-gray-500" />
                    </button>
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">Control Creation Wizard</h1>
                        <p className="text-sm text-gray-500">Follow the steps to create and configure your control</p>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/sox-controls')}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                    Cancel
                </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 max-w-5xl mx-auto w-full px-8 py-12">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-8">
                        {/* Step Indicator */}
                        <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-6">
                            <div className="flex items-center gap-2">
                                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-medium">Step 3 of 3</span>
                            </div>
                            <span className="text-sm text-gray-500">Save Control</span>
                        </div>

                        <h2 className="text-lg font-semibold text-gray-900 mb-1">Save Control</h2>
                        <p className="text-sm text-gray-500 mb-8">
                            Your control is ready to be saved. You can upload evidence files from the Evidence Store tab after creation.
                        </p>

                        {/* Actions */}
                        <div className="flex justify-between pt-6 border-t mt-8">
                            <button
                                onClick={handleBack}
                                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center gap-2"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back
                            </button>
                            <button
                                onClick={handleComplete}
                                disabled={isSubmitting}
                                className={`px-6 py-2.5 text-sm font-medium text-white rounded-lg inline-flex items-center gap-2 transition-colors ${isSubmitting
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Creating Control...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="w-4 h-4" />
                                        Complete Setup
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal for errors and warnings */}
            {modal.open && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${modal.type === 'error' ? 'bg-red-100' : 'bg-yellow-100'}`}>
                                    {modal.type === 'error' ? (
                                        <AlertCircle className="w-5 h-5 text-red-600" />
                                    ) : (
                                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                    )}
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {modal.type === 'error' ? 'Error' : 'Warning'}
                                </h3>
                            </div>
                            <p className="text-sm text-gray-600 mb-6">{modal.message}</p>
                            <div className="flex justify-end gap-3">
                                {modal.type === 'warning' && (
                                    <button
                                        onClick={() => {
                                            setModal({ open: false, type: 'error', message: '' });
                                            navigate('/sox-controls');
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Continue Anyway
                                    </button>
                                )}
                                <button
                                    onClick={() => setModal({ open: false, type: 'error', message: '' })}
                                    className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
                                >
                                    {modal.type === 'error' ? 'Close' : 'Stay Here'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataSourceConfiguration;
